import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    Avatar,
    Box,
    Divider,
    IconButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Typography,
} from '@mui/material';
import {
    Logout as LogoutIcon,
    Person as PersonIcon,
    SwapHoriz as SwapHorizIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import useAccessibleOrganizations from '../hooks/useAccessibleOrganizations';
import { getUserInitials } from '../utils/initials';
import { getHubProfilePath } from '../utils/hubRoutes';

function UserProfileMenu() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { currentUser, logout, organization } = useAuth();
    const { canSwitchOrganizations } = useAccessibleOrganizations();
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    const initials = useMemo(() => getUserInitials(currentUser), [currentUser]);

    const handleOpen = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleLogout = async () => {
        handleClose();
        try {
            await logout();
            navigate('/login');
        } catch (error) {
            console.error('Logout error:', error);
        }
    };

    const handleSwitchOrganizations = () => {
        handleClose();
        navigate('/hubs');
    };

    const handleViewProfile = () => {
        handleClose();
        navigate(getHubProfilePath(organization));
    };

    return (
        <>
            <IconButton
                id="user-profile-button"
                onClick={handleOpen}
                size="small"
                aria-label={t('app.layout.userMenu')}
                aria-controls={open ? 'user-profile-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={open ? 'true' : undefined}
                sx={{ ml: 1 }}
            >
                <Avatar
                    sx={{
                        width: 32,
                        height: 32,
                        bgcolor: 'rgba(255, 255, 255, 0.2)',
                        color: 'inherit',
                        fontSize: '0.875rem',
                        fontWeight: 600,
                    }}
                >
                    {initials}
                </Avatar>
            </IconButton>
            <Menu
                id="user-profile-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                disableScrollLock
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                PaperProps={{
                    sx: { minWidth: 220 },
                }}
                MenuListProps={{
                    'aria-labelledby': 'user-profile-button',
                    autoFocusItem: false,
                }}
            >
                <Box sx={{ px: 2, py: 1.5 }}>
                    <Typography variant="subtitle2" fontWeight={600} noWrap>
                        {currentUser?.displayName || currentUser?.email || '—'}
                    </Typography>
                    {currentUser?.displayName && currentUser?.email && (
                        <Typography variant="caption" color="text.secondary" noWrap>
                            {currentUser.email}
                        </Typography>
                    )}
                </Box>
                <Divider />
                <MenuItem onClick={handleViewProfile}>
                    <ListItemIcon>
                        <PersonIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>{t('app.layout.viewProfile')}</ListItemText>
                </MenuItem>
                {canSwitchOrganizations && (
                    <MenuItem onClick={handleSwitchOrganizations}>
                        <ListItemIcon>
                            <SwapHorizIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>{t('hubs.switchOrganization')}</ListItemText>
                    </MenuItem>
                )}
                <Divider />
                <MenuItem onClick={handleLogout}>
                    <ListItemIcon>
                        <LogoutIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>{t('app.layout.logout')}</ListItemText>
                </MenuItem>
            </Menu>
        </>
    );
}

export default UserProfileMenu;
